import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, ChevronLeft, Check, Sparkles } from "lucide-react";
import Header from "../components/Header";
import MiniFeedbackToast from "../components/MiniFeedbackToast";
import SkinProfilePicker from "../components/SkinProfilePicker";
import EyeShapePicker from "../components/EyeShapePicker";
import { useSession } from "../context/SessionContext";
import { track } from "../lib/analytics";
import { trackFP } from "../lib/firstPartyAnalytics";
import { saveAllergenProfile, fetchStylePreferences, saveStylePreferences } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { readHeroIntent, clearHeroIntent } from "../lib/heroIntent";
import DemoPrivacyNotice from "../components/DemoPrivacyNotice";

// Build the STEPS array from i18n keys so all labels/descriptions
// localize without changing any persistence-key plumbing. The shape +
// `value` strings remain identical to the English-only original — only
// the displayed copy is translated. Backend stores English-keyed `value`s
// regardless of UI language, which keeps existing user profiles compatible.
const buildSteps = (t) => {
  const UNSURE = {
    value: "unsure",
    label: t("onboarding.unsureLabel"),
    description: t("onboarding.unsureDescription"),
  };
  return [
  {
    key: "trouve_intro",
    custom: "trouve_intro",
    title: t("onboarding.steps.trouveIntroTitle"),
    subtitle: t("onboarding.steps.trouveIntroSubtitle"),
    cta: t("onboarding.steps.trouveIntroCta"),
  },
  {
    key: "goal",
    title: t("onboarding.steps.goalTitle"),
    subtitle: t("onboarding.steps.goalSubtitle"),
    options: [
      { value: "soft", label: t("onboarding.steps.goalSoftLabel"), description: t("onboarding.steps.goalSoftDesc") },
      { value: "everyday", label: t("onboarding.steps.goalEverydayLabel"), description: t("onboarding.steps.goalEverydayDesc") },
      { value: "glam", label: t("onboarding.steps.goalGlamLabel"), description: t("onboarding.steps.goalGlamDesc") },
      { value: "explore", label: t("onboarding.steps.goalExploreLabel"), description: t("onboarding.steps.goalExploreDesc") },
    ],
  },
  {
    key: "experience",
    title: t("onboarding.steps.expTitle"),
    subtitle: t("onboarding.steps.expSubtitle"),
    options: [
      { value: "beginner", label: t("onboarding.steps.expBegLabel"), description: t("onboarding.steps.expBegDesc") },
      { value: "some", label: t("onboarding.steps.expSomeLabel"), description: t("onboarding.steps.expSomeDesc") },
      { value: "confident", label: t("onboarding.steps.expConfLabel"), description: t("onboarding.steps.expConfDesc") },
    ],
  },
  {
    key: "beard_shadow",
    title: t("onboarding.steps.beardTitle"),
    subtitle: t("onboarding.steps.beardSubtitle"),
    options: [
      { value: true, label: t("onboarding.steps.beardYes"), description: t("onboarding.steps.beardYesDesc") },
      { value: false, label: t("onboarding.steps.beardNo"), description: t("onboarding.steps.beardNoDesc") },
    ],
  },
  {
    key: "coverage",
    title: t("onboarding.steps.coverageTitle"),
    subtitle: t("onboarding.steps.coverageSubtitle"),
    options: [
      { value: "sheer", label: t("onboarding.steps.coverageSheer"), description: t("onboarding.steps.coverageSheerDesc") },
      { value: "medium", label: t("onboarding.steps.coverageMedium"), description: t("onboarding.steps.coverageMediumDesc") },
      { value: "full", label: t("onboarding.steps.coverageFull"), description: t("onboarding.steps.coverageFullDesc") },
      UNSURE,
    ],
  },
  {
    key: "texture",
    title: t("onboarding.steps.textureTitle"),
    subtitle: t("onboarding.steps.textureSubtitle"),
    options: [
      { value: "liquid", label: t("onboarding.steps.textureLiquid"), description: t("onboarding.steps.textureLiquidDesc") },
      { value: "cream", label: t("onboarding.steps.textureCream"), description: t("onboarding.steps.textureCreamDesc") },
      { value: "powder", label: t("onboarding.steps.texturePowder"), description: t("onboarding.steps.texturePowderDesc") },
      UNSURE,
    ],
  },
  {
    key: "lip_finish",
    title: t("onboarding.steps.lipTitle"),
    subtitle: t("onboarding.steps.lipSubtitle"),
    options: [
      { value: "cream", label: t("onboarding.steps.lipCream"), description: t("onboarding.steps.lipCreamDesc") },
      { value: "satin", label: t("onboarding.steps.lipSatin"), description: t("onboarding.steps.lipSatinDesc") },
      { value: "matte", label: t("onboarding.steps.lipMatte"), description: t("onboarding.steps.lipMatteDesc") },
      { value: "gloss", label: t("onboarding.steps.lipGloss"), description: t("onboarding.steps.lipGlossDesc") },
      UNSURE,
    ],
  },
  {
    key: "eye_finish",
    title: t("onboarding.steps.eyeTitle"),
    subtitle: t("onboarding.steps.eyeSubtitle"),
    options: [
      { value: "matte", label: t("onboarding.steps.eyeMatte"), description: t("onboarding.steps.eyeMatteDesc") },
      { value: "satin", label: t("onboarding.steps.eyeSatin"), description: t("onboarding.steps.eyeSatinDesc") },
      { value: "shimmer", label: t("onboarding.steps.eyeShimmer"), description: t("onboarding.steps.eyeShimmerDesc") },
      { value: "glitter", label: t("onboarding.steps.eyeGlitter"), description: t("onboarding.steps.eyeGlitterDesc") },
      UNSURE,
    ],
  },
  {
    // Eye SHAPE (visual, hooded/monolid/etc) — distinct from eye
    // FINISH above (matte/satin/etc). Stored separately in its own
    // encrypted `/profile/eye-shape` resource, not in style_preferences.
    // Custom renderer because users genuinely can't pick the right
    // answer from text labels alone — we show a tiny SVG of each.
    key: "eye_shape",
    custom: "eye_shape",
    title: t("onboarding.steps.eyeShapeTitle"),
    subtitle: t("onboarding.steps.eyeShapeSubtitle"),
  },
  {
    key: "skip_categories",
    multi: true,
    optional: true,
    title: t("onboarding.steps.skipTitle"),
    subtitle: t("onboarding.steps.skipSubtitle"),
    options: [
      { value: "color_corrector", label: t("onboarding.steps.skipColorCorrector") },
      { value: "contour", label: t("onboarding.steps.skipContour") },
      { value: "bronzer", label: t("onboarding.steps.skipBronzer") },
      { value: "blush", label: t("onboarding.steps.skipBlush") },
      { value: "highlighter", label: t("onboarding.steps.skipHighlighter") },
      { value: "brows", label: t("onboarding.steps.skipBrows") },
      { value: "eyeshadow", label: t("onboarding.steps.skipEyeshadow") },
      { value: "eyeliner", label: t("onboarding.steps.skipEyeliner") },
      { value: "mascara", label: t("onboarding.steps.skipMascara") },
      { value: "lip_liner", label: t("onboarding.steps.skipLipLiner") },
      { value: "lip_coverage", label: t("onboarding.steps.skipLipCoverage") },
      { value: "setting_powder", label: t("onboarding.steps.skipSettingPowder") },
    ],
    cta: t("onboarding.continue"),
  },
  {
    key: "owned_products",
    text: true,
    optional: true,
    title: t("onboarding.steps.ownedTitle"),
    subtitle: t("onboarding.steps.ownedSubtitle"),
    placeholder: t("onboarding.steps.ownedPlaceholder"),
    maxLength: 600,
    cta: t("onboarding.continue"),
  },
  {
    key: "ethics",
    multi: true,
    optional: true,
    title: t("onboarding.steps.ethicsTitle"),
    subtitle: t("onboarding.steps.ethicsSubtitle"),
    options: [
      { value: "cruelty_free", label: t("onboarding.steps.ethicsCrueltyFree"), description: t("onboarding.steps.ethicsCrueltyFreeDesc") },
      { value: "vegan", label: t("onboarding.steps.ethicsVegan"), description: t("onboarding.steps.ethicsVeganDesc") },
    ],
    cta: t("onboarding.continue"),
  },
  {
    // Mirrors the canonical taxonomy in /app/backend/ingredients.py.
    // The backend validates every id we send, so if this list ever
    // drifts the PUT will return 422 — fails safely rather than
    // silently flagging the wrong allergen.
    key: "allergens",
    multi: true,
    optional: true,
    persistTo: "allergens",
    title: t("onboarding.steps.allergensTitle"),
    subtitle: t("onboarding.steps.allergensSubtitle"),
    // Allergens are now localized — labels + descriptions both flow
    // through i18n. INCI / chemical names that have no real-world
    // translation (Methylisothiazolinone, Parabens) are mapped to
    // the same string across locales; allergens with localized
    // packaging conventions (Fragrance → Fragancia, etc.) get the
    // proper translation. See locales/{en,fr,es}.json → onboarding.steps.allergens.
    options: [
      { value: "fragrance", label: t("onboarding.steps.allergens.fragrance"), description: t("onboarding.steps.allergens.fragranceDesc") },
      { value: "essential_oils", label: t("onboarding.steps.allergens.essentialOils"), description: t("onboarding.steps.allergens.essentialOilsDesc") },
      { value: "parabens", label: t("onboarding.steps.allergens.parabens"), description: t("onboarding.steps.allergens.parabensDesc") },
      { value: "formaldehyde_releasers", label: t("onboarding.steps.allergens.formaldehyde"), description: t("onboarding.steps.allergens.formaldehydeDesc") },
      { value: "mit_mci", label: t("onboarding.steps.allergens.mit"), description: t("onboarding.steps.allergens.mitDesc") },
      { value: "lanolin", label: t("onboarding.steps.allergens.lanolin"), description: t("onboarding.steps.allergens.lanolinDesc") },
      { value: "propylene_glycol", label: t("onboarding.steps.allergens.propylene"), description: t("onboarding.steps.allergens.propyleneDesc") },
      { value: "salicylic_acid", label: t("onboarding.steps.allergens.salicylic"), description: t("onboarding.steps.allergens.salicylicDesc") },
      { value: "castor_oil", label: t("onboarding.steps.allergens.castor"), description: t("onboarding.steps.allergens.castorDesc") },
      { value: "nickel", label: t("onboarding.steps.allergens.nickel"), description: t("onboarding.steps.allergens.nickelDesc") },
    ],
    cta: t("onboarding.continue"),
  },
  {
    key: "skin_profile",
    custom: "skin_profile",
    optional: true,
    title: t("onboarding.steps.skinTitle"),
    subtitle: t("onboarding.steps.skinSubtitle"),
    cta: t("onboarding.finish"),
  },
];
};

const Onboarding = ({ ephemeral = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { preferences, setPreferences } = useSession();
  const { user } = useAuth();
  const [stepIdx, setStepIdx] = useState(0);
  const [hydrating, setHydrating] = useState(!ephemeral);
  // Edit mode = the user navigated here via "Refresh my answers" in
  // Settings (`/onboarding?edit=1`). We deliberately do NOT pre-fill
  // their saved answers in this case — the founder reported that
  // pre-selected chips bias the user's choices, and that's especially
  // true when someone is intentionally trying to refresh. They want a
  // clean slate. Captured at mount, not derived per-render, so it
  // stays stable for the lifetime of this onboarding session.
  const [editMode] = useState(() => {
    if (ephemeral) return false;
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("edit") === "1";
  });
  // Refine mode (`/onboarding?refine=1`) = the user came back from
  // their first personalized routine to drop in deferred-profile data
  // (coverage + texture) so the next routine feels more like them.
  // Only TWO questions show in this mode (coverage, then texture) and
  // on finish we route the user straight back to /routine/<from> with
  // a confirmation toast. The full 14-step refine flow is intentionally
  // NOT surfaced from this entry point — keep the loop fast.
  const [refineMode] = useState(() => {
    if (ephemeral) return false;
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("refine") === "1";
  });
  // Routine id we should bounce back to after a successful refine
  // submit. Read from the same querystring (Routine.jsx writes
  // `?refine=1&from=<id>`). We persist it once at mount so a soft
  // navigation later in the flow doesn't lose it.
  const [refineFromRoutineId] = useState(() => {
    if (ephemeral) return null;
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("from") || null;
  });
  // Set to true the instant a refine submit finishes successfully so
  // the unmount cleanup knows NOT to fire `refine_routine_abandoned`.
  const refineCompletedRef = useRef(false);
  // STEPS rebuilds when the locale changes — react-i18next re-renders the
  // component on language switch, so this is automatic. In fast mode
  // (first run, no ?refine=1 and no ?edit=1) we surface an 11-step
  // trust-building flow — the eight essential preference questions
  // plus three quick optional ones — before /capture takes the
  // selfie. The old "3-step fast mode" (intro/goal/experience only)
  // felt shallow: users arrived at /capture without enough signal
  // to personalise coverage, texture, allergens, or skin type. That
  // meant the generated routine had to lean heavily on the CV read
  // and generic defaults. The rebalance below surfaces the trust-
  // building quiz as the CENTRE of the funnel while keeping the
  // selfie step additive.
  //
  // Deliberately EXCLUDED from first-run (still shown in edit mode):
  //   • eye_shape   — custom visual picker; slower + less impactful
  //                   than the other custom picker (skin_profile).
  //   • lip_finish  — style-of-day preference; better collected via
  //                   the routine "swap" affordance after first use.
  //   • eye_finish  — same reasoning as lip_finish.
  //
  // In refine mode we surface ONLY the two progressive-profiling
  // questions the user opted into from /routine: coverage + texture.
  // No intro card, no selfie hand-off — finish writes prefs and
  // bounces back to the routine with a confirmation toast.
  //
  // In edit mode (?edit=1) the full 14-step canonical flow is
  // available so the user can top up every preference dimension.
  const FIRST_RUN_STEP_KEYS = [
    "trouve_intro",     //  1 · welcome card (advance-only)
    "goal",             //  2 · vibe — biases /api/analyze intent bucket
    "experience",       //  3 · pacing — tutorial verbosity + step count
    "beard_shadow",     //  4 · optional add-on colour-correct step
    "coverage",         //  5 · foundation weight
    "texture",          //  6 · liquid / cream / powder preference
    "skip_categories",  //  7 · optional multi — quick taps, dismissible
    "owned_products",   //  8 · optional free text — dismissible
    "ethics",           //  9 · cruelty-free / vegan filter (2 chips)
    "allergens",        // 10 · IMPORTANT — backend hard-filter safety
    "skin_profile",     // 11 · custom picker; drives primer + coverage
  ];
  const ALL_STEPS = buildSteps(t);
  const fastMode = !refineMode && !editMode;
  const STEPS = fastMode
    ? ALL_STEPS.filter((s) => FIRST_RUN_STEP_KEYS.includes(s.key))
    : refineMode
      ? ALL_STEPS.filter((s) => s.key === "coverage" || s.key === "texture")
      : ALL_STEPS;
  const step = STEPS[stepIdx];

  const isLast = stepIdx === STEPS.length - 1;

  // Fire `onboarding_started` once per mount (canonical Step 3 of the
  // Feb 2026 conversion funnel). The `mode` property lets the funnel
  // dashboard split fast first-run vs. progressive-profiling refines.
  // Uses trackFP so Stealth users still appear in the funnel.
  useEffect(() => {
    if (ephemeral) return;
    trackFP("onboarding_started", {
      mode: fastMode ? "fast" : refineMode ? "refine" : "edit",
      auth: user ? "signed_in" : "guest",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abandonment intercept — if the user is past the first question and
  // tries to leave back to home, we give a soft "what made you pause?"
  // prompt before navigating away. Skipped at step 0 (they just opened it).
  const [showAbandonToast, setShowAbandonToast] = useState(false);

  // ── Hero intent fast-skip (iter 79, Jun 25 2026). If the visitor
  // came in through the homepage HeroTrouveChat, sessionStorage will
  // hold their topic + goal mapping + transcript. We:
  //   1. Pre-fill preferences.goal (and `hero_intent` carries forward
  //      to /api/analyze so routine generation knows the topic).
  //   2. Auto-advance past the "goal" step on mount so the user lands
  //      on `experience` directly (then /capture for the selfie).
  //   3. Clear the sessionStorage blob once consumed.
  //
  // This MUST run before the "returning user hydrate + bounce" effect
  // below, otherwise an authed visitor who used the hero chat would
  // skip past Onboarding entirely without ever consuming the intent.
  useEffect(() => {
    if (ephemeral) return;
    if (refineMode || editMode) return;
    const intent = readHeroIntent();
    if (!intent) return;
    setPreferences((prev) => ({
      ...prev,
      goal: prev.goal || intent.goal || "everyday",
      hero_intent: {
        topic_id: intent.topic_id,
        topic_label: intent.topic_label,
        turn1_chip_id: intent.turn1_chip_id || null,
        turn1_chip_label: intent.turn1_chip_label || null,
        free_text: intent.free_text || null,
      },
    }));
    // The hero-intent transcript already gave us the visitor's
    // goal, so we skip past trouve_intro + goal and land them on
    // 'experience'. We look up the index dynamically instead of
    // hard-coding — the fast-mode step list expanded to 11 steps in
    // Feb 27 2026 and any future reordering keeps working.
    const experienceIdx = STEPS.findIndex((s) => s.key === "experience");
    setStepIdx(experienceIdx >= 0 ? experienceIdx : 0);
    trackFP("hero_trouve_intent_consumed", {
      topic_id: intent.topic_id,
      goal: intent.goal,
    });
    clearHeroIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-skip for returning users with saved preferences. The user
  // explicitly asked: "I want users to store their onboarding so they
  // don't have to answer the same 11 questions every time they want
  // a new look." So if they're authed AND have saved prefs AND haven't
  // requested edit-mode (`?edit=1`), we hydrate + bounce to /capture.
  // First-time users + users on `?edit=1` see the full flow.
  //
  // EDIT MODE is bias-free: we do NOT hydrate saved answers when the
  // user explicitly asked to "Refresh my answers" — pre-selecting
  // chips when someone is intentionally redoing their preferences
  // defeats the entire point. We still check the server (so first-time
  // users on `?edit=1` get the same flow as everyone else) but we
  // skip the merge.
  useEffect(() => {
    if (ephemeral) {
      setHydrating(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      if (!user) {
        if (!cancelled) setHydrating(false);
        return;
      }
      try {
        const data = await fetchStylePreferences();
        if (cancelled) return;
        if (data?.preferences && Object.keys(data.preferences).length > 0) {
          if (editMode) {
            // Clean-slate refresh: don't pre-fill. Old answers stay
            // safely on the server until the user finishes the wizard
            // and overwrites them (or bails out, in which case they
            // remain untouched).
            track("onboarding_edit_mode_clean_slate");
          } else if (refineMode) {
            // Refine mode: hydrate so the eventual save merges the
            // user's two new picks (coverage + texture) into their
            // existing prefs instead of clobbering everything else.
            // We deliberately do NOT bounce to /capture — the refine
            // flow stays on this page until they answer the two
            // questions or walk away.
            setPreferences((prev) => ({ ...prev, ...data.preferences }));
          } else {
            // Returning-user shortcut: hydrate + bounce straight to
            // /capture. The hydration is what lets /capture send a
            // fully-populated /api/analyze payload without making the
            // user re-answer.
            setPreferences((prev) => ({ ...prev, ...data.preferences }));
            track("onboarding_skipped_via_saved_preferences");
            toast.success(t("onboarding.autofillToast"));
            navigate("/capture", { replace: true });
            return;
          }
        }
      } catch {
        /* unauthed / no saved prefs → full flow */
      }
      if (!cancelled) setHydrating(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const advance = async (freshOverride = null) => {
    // Merge in any just-selected value the caller passes explicitly.
    // React state is asynchronous — when `select(value)` schedules a
    // setPreferences and then setTimeout(advance, 250), the timer's
    // captured `advance` closure still reads the STALE `preferences`
    // from the render when select was called. Callers pass the fresh
    // `{ [key]: value }` here so save+navigate see the value the user
    // actually clicked.
    const prefs = freshOverride
      ? { ...preferences, ...freshOverride }
      : preferences;
    track("onboarding_step_completed", { step: step.key });

    // Canonical Feb 2026 conversion-funnel events. The fast first-run
    // flow surfaces exactly two questions before /capture:
    //   • goal       → fires `question_1_answered`
    //   • experience → fires `question_2_answered`
    // Both events carry the answer as a property so we never need a
    // new event name for new chip values. Tracked regardless of mode
    // so refine/edit flows are still measurable, but the funnel
    // dashboard scopes to `mode=fast` for the headline conversion %.
    if (step.key === "goal") {
      trackFP("question_1_answered", {
        question: "goal",
        answer: prefs.goal ?? null,
      });
    } else if (step.key === "experience") {
      trackFP("question_2_answered", {
        question: "experience",
        answer: prefs.experience ?? null,
      });
    }

    // When this step has `persistTo: "allergens"`, write the user's
    // selection to the backend before moving on. We don't block the
    // navigation on a network error — the user can re-set their
    // allergens in Settings if the request fails.
    if (!ephemeral && step.persistTo === "allergens") {
      const list = Array.isArray(prefs.allergens)
        ? prefs.allergens
        : [];
      try {
        await saveAllergenProfile(list);
        track("allergen_profile_saved", { count: list.length });
      } catch {
        track("allergen_profile_save_failed");
      }
    }

    if (!isLast) setStepIdx(stepIdx + 1);
    else {
      track("onboarding_finished");
      // Refine mode completes here: persist the two new answers
      // (coverage + texture) on top of any existing saved prefs,
      // fire the engagement event, then bounce the user back to the
      // routine with a warm confirmation toast.
      if (refineMode) {
        if (user) {
          try {
            await saveStylePreferences({
              goal: prefs.goal,
              experience: prefs.experience,
              beard_shadow: prefs.beard_shadow,
              notes: prefs.notes,
              lip_finish: prefs.lip_finish,
              eye_finish: prefs.eye_finish,
              coverage: prefs.coverage,
              texture: prefs.texture,
              skip_categories: prefs.skip_categories,
              ethics: prefs.ethics,
              owned_products: prefs.owned_products,
            });
            track("style_preferences_saved");
          } catch {
            /* save best-effort; in-memory prefs still feed the next
               analyze call within this session even if the persist hop
               fails (e.g. transient network blip). */
          }
        }
        refineCompletedRef.current = true;
        trackFP("refine_routine_completed", {
          coverage: prefs.coverage ?? null,
          texture: prefs.texture ?? null,
          had_routine_id: !!refineFromRoutineId,
        });
        toast.success(t("routine.refineToastTitle"), {
          description: t("routine.refineToastBody"),
        });
        if (refineFromRoutineId) {
          navigate(`/routine/${refineFromRoutineId}`, { replace: true });
        } else {
          navigate("/my-routines", { replace: true });
        }
        return;
      }
      // Persist style preferences so the next "new look" skips the
      // 11 questions. Fire-and-forget — never blocks navigation.
      if (user && !ephemeral) {
        saveStylePreferences({
          goal: prefs.goal,
          experience: prefs.experience,
          beard_shadow: prefs.beard_shadow,
          notes: prefs.notes,
          lip_finish: prefs.lip_finish,
          eye_finish: prefs.eye_finish,
          coverage: prefs.coverage,
          texture: prefs.texture,
          skip_categories: prefs.skip_categories,
          ethics: prefs.ethics,
          owned_products: prefs.owned_products,
        })
          .then(() => track("style_preferences_saved"))
          .catch(() => {});
      }
      navigate(ephemeral ? "/demo/capture" : "/capture");
    }
  };

  const select = (value) => {
    if (step.multi) {
      // toggle membership in the array
      const cur = Array.isArray(preferences[step.key]) ? preferences[step.key] : [];
      const next = cur.includes(value)
        ? cur.filter((v) => v !== value)
        : [...cur, value];
      setPreferences({ ...preferences, [step.key]: next });
      return;
    }
    setPreferences({ ...preferences, [step.key]: value });
    // Pass the fresh selection through so the setTimeout-captured
    // `advance` closure doesn't fire with stale preferences (React
    // state update is asynchronous — see comment in `advance`).
    setTimeout(() => advance({ [step.key]: value }), 250);
  };

  const back = () => {
    if (stepIdx > 0) {
      setStepIdx(stepIdx - 1);
      return;
    }
    // Refine flow: backing out at step 0 returns the user to their
    // routine instead of the homepage. Unmount cleanup fires the
    // `refine_routine_abandoned` event automatically.
    if (refineMode) {
      if (refineFromRoutineId) {
        navigate(`/routine/${refineFromRoutineId}`);
      } else {
        navigate("/my-routines");
      }
      return;
    }
    // At step 0 trying to go home → check if they had any meaningful
    // engagement (deepestStep >= 2 means they answered at least 2
    // questions, then walked back). If yes, intercept with a soft toast.
    if (deepestStep.current >= 2 && !showAbandonToast) {
      setShowAbandonToast(true);
      return;
    }
    navigate("/");
  };

  // Track the deepest step the user has ever reached this session, so
  // we can recognize someone who explored several questions and then
  // walked all the way back.
  const deepestStep = useRef(0);
  useEffect(() => {
    if (stepIdx > deepestStep.current) deepestStep.current = stepIdx;
  }, [stepIdx]);

  // Refine-flow abandonment: fires exactly once, on unmount, IF the
  // user landed in refine mode and did NOT submit the second question.
  // This pairs with `refine_routine_clicked` (Routine.jsx) and
  // `refine_routine_completed` (advance handler above) to make the
  // engagement loop measurable end-to-end without a backend signal.
  useEffect(() => {
    if (!refineMode) return undefined;
    return () => {
      if (!refineCompletedRef.current) {
        trackFP("refine_routine_abandoned", {
          deepest_step: deepestStep.current,
          had_routine_id: !!refineFromRoutineId,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Each new step lands at the top of the page. Without this, a user
  // who scrolled to the bottom of a long step (e.g. 12-chip allergens
  // grid) and then hit Continue would land mid-page on the next step,
  // with its CTA potentially off-screen — making the flow feel
  // disorienting. `instant` (not `smooth`) keeps the transition tight
  // and pairs cleanly with the AnimatePresence fade.
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [stepIdx]);

  const skip = () => {
    track("onboarding_skipped", { step: step.key });
    // For optional multi-select steps, "skip" leaves the array empty and
    // moves on. For "unsure" single-select steps the user just clicks
    // the unsure option directly. For free-text steps we clear the
    // field so a stale value from a previous session doesn't leak
    // through.
    if (step.multi) {
      setPreferences({ ...preferences, [step.key]: [] });
    } else if (step.text) {
      setPreferences({ ...preferences, [step.key]: "" });
    }
    advance();
  };

  const isSelected = (val) => {
    const cur = preferences[step.key];
    if (step.multi) return Array.isArray(cur) && cur.includes(val);
    return cur === val;
  };

  // Hydration in progress — render a slim placeholder while we check
  // for saved preferences. Returning users see this for ~150ms before
  // we redirect to /capture; first-timers see it for ~150ms before the
  // first question pops in.
  if (hydrating) {
    return (
      <div
        className="relative min-h-screen trubeaute-gradient flex items-center justify-center"
        data-testid="onboarding-hydrating"
      >
        <Header />
        <div className="text-[#9B6A6C] text-sm tracking-wide">
          {t("onboarding.hydrating")}
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen trubeaute-gradient">
      <Header />

      {ephemeral && (
        <div className="pt-20 px-4 sm:px-6">
          <DemoPrivacyNotice />
        </div>
      )}

      <section className={`${ephemeral ? "pt-8" : "pt-24 sm:pt-32"} pb-20 px-4 sm:px-6`} data-testid="onboarding-root">
        <div className="max-w-xl mx-auto">
          {/* progress dots */}
          <div className="flex items-center justify-center gap-2 mb-14 flex-wrap">
            {STEPS.map((s, i) => (
              <div
                key={s.key}
                className={`progress-dot ${
                  i === stepIdx ? "active" : i < stepIdx ? "done" : ""
                }`}
                data-testid={`onboarding-progress-${i}`}
              />
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={step.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
            >
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#2C2123] text-center leading-tight mb-3"
                data-testid="onboarding-step-title"
              >
                {step.title}
              </h2>
              <p className="text-center text-[14px] sm:text-base text-[#5C4B51] mb-8 sm:mb-10 leading-relaxed">
                {step.subtitle}
              </p>

              <div className={step.custom ? "" : step.multi ? "grid grid-cols-2 gap-2" : step.text ? "" : "space-y-3"}>
                {step.custom === "trouve_intro" ? (
                  <div
                    className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#F2E8E9] to-[#FAF5F1] border border-[#EADDD7] p-7 sm:p-9 text-center"
                    data-testid="onboarding-trouve-intro"
                  >
                    {/* Soft glow ring behind the avatar */}
                    <div className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 h-32 w-32 rounded-full bg-[#D5A8A4]/30 blur-3xl" />
                    <div className="relative">
                      <div className="mx-auto h-16 w-16 rounded-full bg-white border border-[#EADDD7] flex items-center justify-center mb-5 shadow-[0_6px_20px_rgba(155,106,108,0.18)]">
                        <Sparkles className="h-7 w-7 text-[#9B6A6C]" />
                      </div>
                      <p className="text-[15px] sm:text-base text-[#3D2F33] leading-relaxed max-w-md mx-auto">
                        {t("onboarding.steps.trouveIntroBody1")}
                      </p>
                      <p className="text-sm text-[#5C4B51] leading-relaxed max-w-md mx-auto mt-3 italic">
                        {t("onboarding.steps.trouveIntroBody2")}
                      </p>
                      <button
                        onClick={advance}
                        className="mt-7 inline-flex items-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-7 py-3.5 text-base transition-all shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5"
                        data-testid="onboarding-trouve-intro-cta"
                      >
                        {step.cta || t("onboarding.steps.trouveIntroCta")}
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : step.custom === "skin_profile" ? (
                  <div className="rounded-2xl border border-[#EADDD7] bg-white/70 backdrop-blur-sm p-6">
                    <SkinProfilePicker
                      mode="onboarding"
                      authed={!ephemeral}
                      // Edit-mode = "Refresh my answers" path → don't
                      // pre-fill saved skin profile. Picker stays blank
                      // until user makes a fresh, unbiased pick.
                      skipHydration={ephemeral || editMode}
                      onSaved={(profile) => {
                        if (ephemeral) {
                          setPreferences((prev) => ({
                            ...prev,
                            skin_concerns: profile?.skinConcerns || [],
                            life_stage: profile?.lifeStage || null,
                          }));
                        }
                        track("skin_profile_saved");
                        advance();
                      }}
                      onSkip={() => {
                        track("onboarding_skipped", { step: step.key });
                        advance();
                      }}
                    />
                  </div>
                ) : step.custom === "eye_shape" ? (
                  <EyeShapePicker onAdvance={advance} skipHydration={editMode} />
                ) : step.text ? (
                  <div>
                    <textarea
                      value={preferences[step.key] || ""}
                      onChange={(e) =>
                        setPreferences({
                          ...preferences,
                          [step.key]: e.target.value.slice(
                            0,
                            step.maxLength || 600,
                          ),
                        })
                      }
                      placeholder={step.placeholder}
                      rows={5}
                      maxLength={step.maxLength || 600}
                      className="w-full rounded-2xl border border-[#EADDD7] bg-white p-4 text-[15px] leading-relaxed text-[#2C2123] placeholder:text-[#B8A8AB] focus:border-[#9B6A6C] focus:outline-none focus:ring-4 focus:ring-[#F2E8E9] transition-all"
                      data-testid={`onboarding-text-${step.key}`}
                    />
                    <div className="flex items-center justify-between text-xs text-[#8A7D82] mt-2 px-1">
                      <span>{t("onboarding.ownedHint")}</span>
                      <span className="tabular-nums shrink-0 ml-3">
                        {(preferences[step.key] || "").length}/
                        {step.maxLength || 600}
                      </span>
                    </div>
                  </div>
                ) : (
                  step.options.map((opt) => {
                    const selected = isSelected(opt.value);
                    return (
                      <button
                        key={String(opt.value)}
                        onClick={() => select(opt.value)}
                        className={`text-left rounded-2xl ${step.multi ? "p-4" : "p-5"} border transition-all duration-300 ${
                          selected
                            ? "bg-[#F2E8E9] border-[#9B6A6C] shadow-[0_6px_24px_rgba(155,106,108,0.2)]"
                            : "bg-white border-[#EADDD7] hover:border-[#D5A8A4] hover:-translate-y-0.5"
                        }`}
                        data-testid={`onboarding-option-${step.key}-${String(opt.value)}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className={`font-medium text-[#2C2123] ${step.multi ? "text-sm" : "text-lg"}`}>
                              {opt.label}
                            </div>
                            {opt.description && (
                              <div className="text-sm text-[#5C4B51] mt-0.5">
                                {opt.description}
                              </div>
                            )}
                          </div>
                          {step.multi ? (
                            <span
                              className={`h-5 w-5 rounded-md border flex items-center justify-center shrink-0 ${
                                selected
                                  ? "bg-[#9B6A6C] border-[#9B6A6C] text-white"
                                  : "border-[#D5A8A4]"
                              }`}
                            >
                              {selected && <Check className="h-3 w-3" />}
                            </span>
                          ) : (
                            <ChevronRight
                              className={`h-5 w-5 shrink-0 transition-colors ${
                                selected ? "text-[#9B6A6C]" : "text-[#C2918D]"
                              }`}
                            />
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {/* For multi-select, text, and other optional steps we need
                  an explicit Continue / Skip row. */}
              {(step.multi || step.text) && (
                <div className="flex items-center justify-between mt-8 gap-3">
                  {step.optional && (
                    <button
                      onClick={skip}
                      className="text-sm text-[#8A7D82] hover:text-[#5D4050] transition-colors"
                      data-testid="onboarding-skip-btn"
                    >
                      {t("onboarding.skip")}
                    </button>
                  )}
                  <button
                    onClick={advance}
                    className="ml-auto inline-flex items-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-6 py-3 text-sm transition-all shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5"
                    data-testid="onboarding-continue-btn"
                  >
                    {step.cta || t("onboarding.continue")}
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}

              <div className="flex items-center justify-between mt-10">
                <button
                  onClick={back}
                  className="inline-flex items-center gap-1 text-sm text-[#5C4B51] hover:text-[#9B6A6C] transition-colors"
                  data-testid="onboarding-back-btn"
                >
                  <ChevronLeft className="h-4 w-4" />
                  {stepIdx === 0 ? t("onboarding.backHome") : t("onboarding.back")}
                </button>
                <span className="text-xs uppercase tracking-[0.2em] text-[#8A7D82]">
                  {t("onboarding.stepOf", { current: stepIdx + 1, total: STEPS.length })}
                </span>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Abandonment intercept — shown when a user who answered ≥2
          questions tries to go home before finishing. After the toast
          closes (submit / dismiss / auto-dismiss), we let them leave. */}
      <AnimatePresence>
        {showAbandonToast && (
          <MiniFeedbackToast
            context="onboarding_abandoned"
            onClose={() => {
              setShowAbandonToast(false);
              navigate("/");
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Onboarding;
